import * as React from "react";

import { selectIsPreviewMode } from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";

import {
  selectCanvasDeltaXY,
  selectCanvasScale,
  selectCanvasTransition,
  selectCanvasWrapperFrame,
} from "./canvas-reducer";

/**
 * NOTE (Chance 2024-03-20): Previously we were assigning transform/width/height
 * values as inline styles. After doing a little research, I found an article
 * that showed some benchmarks indicating that setting CSS variables using
 * Element.style.setProperty performs quite a bit better than updating inline
 * styles directly. This sent me down a rabbit hole and in doing some benchmarks
 * of my own, I found that the fastest method seems to be using the style
 * attribute directly and assigning all of the values via a single style string.
 * my own testing.
 *
 * @see https://jsperf.app/vewuxa
 */
export function useUpdateFramePositionStyles(props: {
  width: number;
  height: number;
}) {
  const canvasScale = useEditorSelector(selectCanvasScale);
  const { deltaX, deltaY } = useEditorSelector(selectCanvasDeltaXY);
  const isPreviewMode = useEditorSelector(selectIsPreviewMode);
  const transition = useEditorSelector(selectCanvasTransition);
  const frameElement = useEditorSelector(selectCanvasWrapperFrame);

  React.useEffect(() => {
    if (!frameElement) {
      return;
    }

    let style: string;
    if (isPreviewMode) {
      const frameWidth = props.width;
      const innerWidth = window.innerWidth;
      let translate: string;
      if (innerWidth < frameWidth) {
        translate = "0px";
      } else {
        translate = `calc(50vw - ${Math.floor(frameWidth / 2)}px)`;
      }

      const width = `${frameWidth}px`;
      const transform = `translate3d(${translate}, 0, 0px)`;
      style = `height: 100%; width: ${width}; transform: ${transform}; transition: ${transition}`;
    } else {
      const width = `${props.width}px`;
      const height = `${props.height}px`;
      const transform = `translate3d(${deltaX}px, ${deltaY}px, 0px) scale(${canvasScale})`;
      style = `width: ${width}; height: ${height}; transform: ${transform}; transition: ${transition}`;
    }

    // While technically `style` is a readonly property, you can still assign
    // to it with a string value. It overrides all styles so this could be
    // error prone if we update the styles elsewhere, so we just need to be
    // careful here.
    // @ts-ignore
    frameElement.style = style;
  }, [
    props.height,
    props.width,
    canvasScale,
    deltaX,
    deltaY,
    isPreviewMode,
    frameElement,
    transition,
  ]);
}
